import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  User, Briefcase, MapPin, Clock, GraduationCap, Code, Award,
  CheckCircle, XCircle, Phone, Pause, Loader2, AlertTriangle,
  ChevronRight, Star, Calendar, Building2, Globe
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HRReviewPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [requestNote, setRequestNote] = useState('');
  const [actionTaken, setActionTaken] = useState(null);
  const [interviewToken, setInterviewToken] = useState(null);

  useEffect(() => {
    loadData();
  }, [token]);

  const loadData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hr-pipeline/review/${token}`);
      if (res.status === 410) {
        setError('expired');
        return;
      }
      if (!res.ok) throw new Error('Link not found');
      const result = await res.json();
      setData(result);
      if (result.status !== 'pending') {
        setActionTaken(result.status);
      }
    } catch (err) {
      setError('not_found');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action, reason = null, note = null) => {
    setActionLoading(true);
    try {
      const formData = new FormData();
      formData.append('action', action);
      if (reason) formData.append('reason', reason);
      if (note) formData.append('note', note);

      const res = await fetch(`${API_URL}/api/hr-pipeline/action/${token}`, {
        method: 'POST',
        body: formData
      });

      if (!res.ok) throw new Error('Action failed');
      
      const result = await res.json();
      setActionTaken(action);
      
      if (action === 'shortlisted' && result.interview_token) {
        setInterviewToken(result.interview_token);
        toast.success('Candidate shortlisted! Now schedule an interview.');
      } else {
        toast.success(`Action recorded: ${action.replace('_', ' ')}`);
      }
      
      setShowRejectModal(false);
      setShowRequestModal(false);
    } catch (err) {
      toast.error('Failed to record action');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error === 'expired') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-800 mb-2">Link Expired</h1>
            <p className="text-slate-600">This review link has expired. Please contact the recruiter for a new link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-800 mb-2">Link Not Found</h1>
            <p className="text-slate-600">This review link is invalid or has been removed.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { candidate, job, match, matching_points, skill_matches, hours_remaining } = data;
  const match_score = match?.score || 0;

  // If action already taken and shortlisted, show interview scheduling
  if (actionTaken === 'shortlisted' && interviewToken) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-800 mb-2">Candidate Shortlisted!</h1>
            <p className="text-slate-600 mb-6">Now schedule an interview with {candidate.name}.</p>
            <Button onClick={() => navigate(`/interview-schedule/${interviewToken}`)} className="w-full">
              <Calendar className="w-4 h-4 mr-2" />
              Schedule Interview
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (actionTaken && actionTaken !== 'pending') {
    const actionMessages = {
      shortlisted: { icon: CheckCircle, color: 'text-green-500', message: 'You have shortlisted this candidate.' },
      rejected: { icon: XCircle, color: 'text-red-500', message: 'You have rejected this candidate.' },
      info_requested: { icon: Phone, color: 'text-blue-500', message: 'You have requested more information.' },
      on_hold: { icon: Pause, color: 'text-amber-500', message: 'You have put this candidate on hold.' }
    };
    const action = actionMessages[actionTaken] || actionMessages.on_hold;
    const Icon = action.icon;

    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Icon className={`w-16 h-16 ${action.color} mx-auto mb-4`} />
            <h1 className="text-xl font-bold text-slate-800 mb-2">Action Recorded</h1>
            <p className="text-slate-600">{action.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-slate-500">Candidate Review</span>
          <Badge variant="outline" className="text-amber-600 border-amber-300">
            <Clock className="w-3 h-3 mr-1" />
            {hours_remaining}h left
          </Badge>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Match Score Card */}
        <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Match Score</p>
                <p className="text-3xl font-bold">{Math.round(match_score)}%</p>
              </div>
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                <Star className="w-8 h-8 text-white" />
              </div>
            </div>
            <div className="mt-3 bg-white/20 rounded-full h-2">
              <div 
                className="bg-white rounded-full h-2 transition-all" 
                style={{ width: `${match_score}%` }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Candidate Info */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                {candidate.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-slate-800 truncate">{candidate.name}</h1>
                <p className="text-slate-600">{candidate.current_designation}</p>
                <div className="flex flex-wrap gap-2 mt-2 text-sm text-slate-500">
                  {candidate.current_location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {candidate.current_location}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Briefcase className="w-3 h-3" />
                    {candidate.total_experience_years} yrs exp
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Job Info */}
        <Card className="bg-slate-50">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Position</p>
            <h2 className="font-semibold text-slate-800">{job.title}</h2>
            {job.company_name && (
              <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                <Building2 className="w-3 h-3" />
                {job.company_name}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Why Good Fit */}
        {matching_points?.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Why This Candidate Fits
              </h3>
              <ul className="space-y-2">
                {matching_points.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <ChevronRight className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    {point}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* AI Summary */}
        {candidate.ai_summary && (
          <Card className="border-indigo-200 bg-indigo-50/30">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <Star className="w-5 h-5 text-indigo-500" />
                AI Executive Summary
              </h3>
              <div 
                className="text-sm text-slate-700 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: candidate.ai_summary }}
              />
              {candidate.estimated_ctc && (
                <div className="mt-3 pt-3 border-t border-indigo-200">
                  <p className="text-sm text-slate-600">
                    <strong>Estimated CTC:</strong> ₹{(candidate.estimated_ctc / 100000).toFixed(1)} LPA
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Professional Summary */}
        {candidate.summary && !candidate.ai_summary && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <User className="w-5 h-5 text-slate-500" />
                Professional Summary
              </h3>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{candidate.summary}</p>
            </CardContent>
          </Card>
        )}

        {/* Skills */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Code className="w-5 h-5 text-blue-500" />
              Technical Skills
            </h3>
            <div className="flex flex-wrap gap-2">
              {(candidate.technical_skills || []).slice(0, 15).map((skill, i) => {
                const isMatched = skill_matches?.some(s => s.skill?.toLowerCase() === skill?.toLowerCase() && s.status === 'matched');
                return (
                  <Badge 
                    key={i} 
                    variant="outline"
                    className={isMatched ? 'bg-green-50 border-green-300 text-green-700' : ''}
                  >
                    {isMatched && <CheckCircle className="w-3 h-3 mr-1" />}
                    {skill}
                  </Badge>
                );
              })}
            </div>
            
            {/* Skill Match Breakdown */}
            {skill_matches?.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-slate-600 mb-2">Skill Match Breakdown</p>
                <div className="space-y-1">
                  {skill_matches.slice(0, 8).map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700">{s.skill}</span>
                      {s.status === 'matched' ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-400" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Education */}
        {candidate.education?.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-purple-500" />
                Education
              </h3>
              <div className="space-y-3">
                {candidate.education.slice(0, 3).map((edu, i) => (
                  <div key={i} className="text-sm">
                    <p className="font-medium text-slate-800">
                      {edu.degree_type || edu.degree || edu.qualification || (typeof edu === 'string' ? edu : 'Degree')}
                    </p>
                    {(edu.institution_name || edu.institution) && (
                      <p className="text-slate-600">{edu.institution_name || edu.institution}</p>
                    )}
                    {(edu.field_of_study) && (
                      <p className="text-slate-500">{edu.field_of_study}</p>
                    )}
                    {(edu.graduation_year || edu.year) && (
                      <p className="text-slate-400 text-xs">{edu.graduation_year || edu.year}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Experience */}
        {candidate.work_experience?.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-amber-500" />
                Experience
              </h3>
              <div className="space-y-4">
                {candidate.work_experience.map((exp, i) => (
                  <div key={i} className="border-l-2 border-slate-200 pl-3">
                    <p className="font-medium text-slate-800">
                      {exp.title || exp.job_title || exp.designation || (typeof exp === 'string' ? exp : 'Position')}
                    </p>
                    <p className="text-sm text-slate-600">
                      {exp.company || exp.company_name || ''}
                    </p>
                    {(exp.duration || exp.start_date) && (
                      <p className="text-xs text-slate-500">
                        {exp.duration || `${exp.start_date || ''} - ${exp.end_date || 'Present'}`}
                      </p>
                    )}
                    {exp.description && (
                      <p className="text-sm text-slate-600 mt-1">{exp.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Certifications */}
        {candidate.certifications?.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                Certifications
              </h3>
              <div className="flex flex-wrap gap-2">
                {candidate.certifications.map((cert, i) => (
                  <Badge key={i} variant="outline">
                    {typeof cert === 'string' ? cert : (cert.certification_name || cert.name || 'Certification')}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Additional Skills */}
        {(candidate.soft_skills?.length > 0 || candidate.programming_languages?.length > 0 || 
          candidate.frameworks_libraries?.length > 0 || candidate.cloud_platforms?.length > 0 ||
          candidate.databases?.length > 0) && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <Code className="w-5 h-5 text-cyan-500" />
                Additional Skills & Technologies
              </h3>
              <div className="space-y-3">
                {candidate.programming_languages?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase mb-1">Programming Languages</p>
                    <div className="flex flex-wrap gap-1">
                      {candidate.programming_languages.map((lang, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{lang}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {candidate.frameworks_libraries?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase mb-1">Frameworks & Libraries</p>
                    <div className="flex flex-wrap gap-1">
                      {candidate.frameworks_libraries.map((fw, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{fw}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {candidate.cloud_platforms?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase mb-1">Cloud Platforms</p>
                    <div className="flex flex-wrap gap-1">
                      {candidate.cloud_platforms.map((cloud, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{cloud}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {candidate.databases?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase mb-1">Databases</p>
                    <div className="flex flex-wrap gap-1">
                      {candidate.databases.map((db, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{db}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {candidate.soft_skills?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase mb-1">Soft Skills</p>
                    <div className="flex flex-wrap gap-1">
                      {candidate.soft_skills.map((skill, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{skill}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Languages & Awards */}
        {(candidate.language_proficiency?.length > 0 || candidate.awards_recognition?.length > 0) && (
          <Card>
            <CardContent className="p-4">
              {candidate.language_proficiency?.length > 0 && (
                <div className="mb-4">
                  <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-blue-500" />
                    Languages
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {candidate.language_proficiency.map((lang, i) => (
                      <Badge key={i} variant="outline">{typeof lang === 'string' ? lang : lang.language}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {candidate.awards_recognition?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                    <Award className="w-5 h-5 text-yellow-500" />
                    Awards & Recognition
                  </h3>
                  <ul className="text-sm text-slate-700 space-y-1">
                    {candidate.awards_recognition.slice(0, 5).map((award, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Star className="w-3 h-3 text-yellow-500 mt-1 flex-shrink-0" />
                        {typeof award === 'string' ? award : award.title || award.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Notice Period & Expected Salary */}
        {(candidate.notice_period_days || candidate.expected_salary) && (
          <Card className="bg-slate-50">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                {candidate.notice_period_days && (
                  <div>
                    <p className="text-slate-500">Notice Period</p>
                    <p className="font-medium text-slate-800">{candidate.notice_period_days} days</p>
                  </div>
                )}
                {candidate.expected_salary && (
                  <div>
                    <p className="text-slate-500">Expected Salary</p>
                    <p className="font-medium text-slate-800">{candidate.expected_salary}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="sticky bottom-0 bg-white border-t p-4 -mx-4 mt-6 pb-[calc(1rem+env(safe-area-inset-bottom,0px))]">
          <div className="grid grid-cols-2 gap-3 max-w-2xl mx-auto">
            <Button 
              onClick={() => handleAction('shortlisted')}
              disabled={actionLoading}
              className="bg-green-600 hover:bg-green-700 max-md:min-h-[48px]"
              data-testid="shortlist-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Shortlist
            </Button>
            <Button 
              onClick={() => setShowRejectModal(true)}
              disabled={actionLoading}
              variant="destructive"
              className="max-md:min-h-[48px]"
              data-testid="reject-btn"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
            <Button 
              onClick={() => setShowRequestModal(true)}
              disabled={actionLoading}
              variant="outline"
              className="max-md:min-h-[48px]"
              data-testid="request-info-btn"
            >
              <Phone className="w-4 h-4 mr-2" />
              Request Info
            </Button>
            <Button 
              onClick={() => handleAction('on_hold')}
              disabled={actionLoading}
              variant="outline"
              className="max-md:min-h-[48px]"
              data-testid="hold-btn"
            >
              <Pause className="w-4 h-4 mr-2" />
              Hold
            </Button>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Candidate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Please provide a reason for rejection (optional):</p>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g., Skills mismatch, experience level not suitable..."
              rows={3}
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowRejectModal(false)}>Cancel</Button>
              <Button 
                variant="destructive" 
                onClick={() => handleAction('rejected', rejectReason)}
                disabled={actionLoading}
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Reject'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Request Info Modal */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request More Information</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Request candidate contact details. The recruiter will be notified.</p>
            <Textarea
              value={requestNote}
              onChange={(e) => setRequestNote(e.target.value)}
              placeholder="Any specific information you need? (optional)"
              rows={3}
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowRequestModal(false)}>Cancel</Button>
              <Button 
                onClick={() => handleAction('info_requested', null, requestNote)}
                disabled={actionLoading}
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Request'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
